import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { Licence } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function LicenceDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const navigate = useNavigate();
  const isEnforcementOfficer = hasAnyRole(persona?.roles, M14_ROLES.ENFORCEMENT_OFFICER);
  const licenceQ = useApiQuery<Licence>(`/api/v1/m14/licences/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [breachDescription, setBreachDescription] = useState('');
  const [showEnforce, setShowEnforce] = useState(false);

  if (licenceQ.loading) return <Spinner label="Loading licence…" />;
  if (licenceQ.error || !licenceQ.data) {
    return (
      <div>
        <Link to="/licensing/licences" className="text-sm font-semibold text-brand-600 hover:underline">← Back to licences</Link>
        <div className="mt-4"><ErrorNote message={licenceQ.error ?? 'Licence not found'} /></div>
      </div>
    );
  }
  const licence = licenceQ.data;

  const openEnforcement = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ id: string }>(`/api/v1/m14/licences/${id}/enforcement`, { breachDescription });
      navigate(`/licensing/enforcement/${res.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open enforcement case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/licensing/licences" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to licences</Link>
      <PageHeader
        title={licence.serial}
        subtitle={`${licence.classCode.replaceAll('_', ' ')} · ${licence.holderType} ${licence.holderId}`}
        actions={<div className="flex gap-2">{licence.trafficLight ? <StatusBadge status={licence.trafficLight} /> : null}<StatusBadge status={licence.status} /></div>}
      />

      <div className="card mb-5 p-5">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div><dt className="text-xs uppercase text-slate-400">Valid</dt><dd>{formatDate(licence.validFrom)} – {formatDate(licence.validTo)}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Premises</dt><dd>{licence.premisesAddress ?? '—'}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Public register</dt><dd>{licence.publicRegisterVisible ? 'Visible' : 'Hidden'}</dd></div>
        </dl>
        {licence.conditions.length > 0 ? (
          <div className="mt-2 text-xs text-slate-600">Conditions: {licence.conditions.join(', ')}</div>
        ) : null}
        {licence.suspendReason ? <div className="mt-2"><ErrorNote message={`Suspended: ${licence.suspendReason}`} /></div> : null}
        {licence.revokedReason ? <div className="mt-2"><ErrorNote message={`Revoked: ${licence.revokedReason}`} /></div> : null}
      </div>

      {isEnforcementOfficer ? (
        <div className="card p-5">
          <h2 className="mb-1 text-sm font-bold text-slate-700">Open an enforcement case</h2>
          {error ? <div className="mb-2"><ErrorNote message={error} /></div> : null}
          {showEnforce ? (
            <div className="space-y-2">
              <Field label="Breach description"><textarea className="field-input" rows={2} value={breachDescription} onChange={(e) => setBreachDescription(e.target.value)} /></Field>
              <button className="btn-danger" onClick={openEnforcement} disabled={busy || breachDescription.length < 5}>{busy ? 'Opening…' : 'Open case'}</button>
            </div>
          ) : (
            <button className="btn-danger" onClick={() => setShowEnforce(true)}>Open enforcement case</button>
          )}
        </div>
      ) : null}
      <InfoNote message="Enforcement cases against this licence are tracked in Licensing › Enforcement." />
    </div>
  );
}
